import {observer} from 'mobx-react-lite'
import React, {useCallback} from 'react'
import {Timeout} from 'yougile-util'
import {TaskColor} from 'yougile-model'
import {IconButtonNew} from '@components/common/base/buttons'
import {CheckboxNew} from '@components/common/base/checkbox-new/checkbox-new'
import {Cell} from '@components/common/sandbox/list-view/components/cell'
import {DoneCounter} from '@components/common/sandbox/list-view/components/done-counter'
import {ItemMenuButton} from '@components/common/sandbox/list-view/components/item-menu-button'
import {ItemName} from '@components/common/sandbox/list-view/components/item-name'
import {useListStore} from '@components/common/sandbox/list-view/list-store'
import * as Tw from '@components/common/sandbox/list-view/tw-components'
import {TwHighlighter} from '@components/common/sandbox/list-view/tw-components'
import {
  Identifiable,
  NullableId,
  RowConfig
} from '@components/common/sandbox/list-view/types'

interface FirstCellProps<TData extends Identifiable> {
  row: RowConfig<TData>
  parentId: NullableId
  nestedLevel: number
  /**
   * Whether the row is active (e.g. menu is open)
   */
  isActive: boolean
  childrenRows?: RowConfig<TData>[],
  /**
   * Whether the row is highlighted (unlike active, highlighted is a visual state and not affects the row's logic)
   */
  isHighlighted: boolean
}

export const FirstCell = observer(
  <TData extends Identifiable>(props: FirstCellProps<TData>) => {
    const store = useListStore()
    const {row, parentId, isActive, childrenRows} = props

    const toggleExpand = useCallback(() => {
      store.toggleExpandChildren(row.data.id)
    }, [row.data.id])

    const setDone = useCallback(
      (value: boolean) => {
        store.setDone(row.data, value)
      },
      [row.data]
    )

    const setEdit = useCallback(
      (edit: boolean) => {
        return edit
          ? store.turnOnItemEditing(row.data.id)
          : store.turnOffItemEditing(row.data.id)
      },
      [store, row.data.id]
    )

    const onSubmitValue = useCallback(
      (value: string) => {
        store.updateItemName(row.data.id, value)
      },
      [store, row.data.id]
    )

    const isExpanded = store.isExpanded(row.data.id)
    const isDone = store.isDone(row)

    return (
      <FirstCellMemo
        row={row}
        parentId={parentId}
        isActive={isActive}
        childrenRows={childrenRows}
        isDone={isDone}
        onToggleExpand={toggleExpand}
        isExpanded={isExpanded}
        setDone={setDone}
        name={store.selectName(row.data)}
        archived={store.selectArchived(row.data)}
        isLazy={store.selectLazy(row.data)}
        color={store.selectColor(row.data)}
        editable={store.isEditingPermittedForItem(row)}
        isEditing={store.editedItem === row.data.id}
        setEdit={setEdit}
        onSubmitValue={onSubmitValue}
        completed={isDone}
        fromBackgroundColor={'from-background-hover'}
        tooltipContent={
          store.selectTooltipContent(row.data) || store.selectName(row.data)
        }
        isHighlighted={props.isHighlighted}
      />
    )
  }
)

interface FirstCellMemoProps<TData> {
  row: RowConfig<TData>
  parentId: NullableId
  isActive: boolean
  childrenRows?: RowConfig<TData>[]
  isDone: boolean
  onToggleExpand: () => void
  isExpanded: boolean
  setDone: (value: boolean) => void
  name: string
  archived: boolean
  isLazy: boolean
  color: TaskColor | null
  editable: boolean
  isEditing: boolean
  setEdit: (edit: boolean) => void
  onSubmitValue: (value: string) => void
  completed: boolean
  fromBackgroundColor: string
  tooltipContent: string | undefined,
  isHighlighted: boolean
}

export const FirstCellMemo = React.memo(
  <TData extends Identifiable>(props: FirstCellMemoProps<TData>) => {
    const [temporaryMarketAsDone, setTemporaryMarkAsDone] =
      React.useState(false)
    const temporaryMarkedAsDoneTimerRef = React.useRef<Timeout | undefined>()

    const {
      row,
      parentId,
      isActive,
      childrenRows,
      isDone,
      onToggleExpand,
      isExpanded,
      setDone,
      name,
      archived,
      isLazy,
      color,
      editable,
      isEditing,
      setEdit,
      onSubmitValue,
      completed,
      fromBackgroundColor,
      tooltipContent,
      isHighlighted
    } = props

    return (
      <Cell>
        <TwHighlighter $visible={isHighlighted} />
        <Tw.TwCellFunctions>
          {!row.children ||
            (row.children.length === 0 && <Tw.TwCellFunction />)}
          <Tw.TwCellFunction>
            <ItemMenuButton
              isDone={isDone}
              row={row}
              parentId={parentId}
              forceShow={isActive}
            />
          </Tw.TwCellFunction>
          {row.children && row.children.length > 0 && (
            <Tw.TwCellFunction
              onClick={e => {
                e.stopPropagation()
                onToggleExpand()
              }}
            >
              <IconButtonNew
                icon={
                  isExpanded ? 'IconCustomChevronUp' : 'IconCustomChevronDown'
                }
              />
            </Tw.TwCellFunction>
          )}
          <Tw.TwCellFunction>
            <CheckboxNew
              rounded
              size="big"
              color="green"
              value={isDone || temporaryMarketAsDone}
              onChange={(value, e) => {
                e?.stopPropagation()
                clearTimeout(temporaryMarkedAsDoneTimerRef.current)
                setTemporaryMarkAsDone(value)
                if (value) {
                  temporaryMarkedAsDoneTimerRef.current = setTimeout(() => {
                    setDone(value)
                  }, 350)
                } else {
                  if (isDone) {
                    setDone(value)
                  }
                  clearTimeout(temporaryMarkedAsDoneTimerRef.current)
                }
              }}
              checkboxClassName="transition-[background-color] duration-[450ms]"
            />
          </Tw.TwCellFunction>
        </Tw.TwCellFunctions>
        <DoneCounter rows={childrenRows} hideIfEmpty />
        <ItemName
          value={name}
          isArchived={archived}
          isLazy={isLazy}
          color={color}
          editable={editable}
          isEditing={isEditing}
          setEdit={setEdit}
          onSubmitValue={onSubmitValue}
          completed={completed}
          fromBackgroundColor={fromBackgroundColor}
          tooltipContent={tooltipContent}
        />
      </Cell>
    )
  }
)
